import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

const IconChip = ({ item, unframed = false, size = "sm" }) => {
  const lg = size === "lg";
  return (
    <div
      className={`shrink-0 flex items-center mx-1.5 ${
        lg ? "gap-4 px-7 py-4" : "gap-3 px-5 py-3"
      } ${
        unframed ? "" : "border border-neutral-200 bg-white/60 backdrop-blur-sm"
      }`}
      aria-hidden="true"
    >
      <span
        className={`flex items-center justify-center rounded-full bg-riversol-blue/[0.06] shrink-0 ${
          lg ? "w-12 h-12" : "w-9 h-9"
        }`}
      >
        <img
          src={item.icon}
          alt=""
          className={lg ? "w-[22px] h-[22px]" : "w-5 h-5"}
          loading="lazy"
        />
      </span>
      <span
        className={`font-medium text-neutral-800 whitespace-nowrap ${
          lg ? "text-[14px]" : "text-[13px]"
        }`}
      >
        {item.label}
      </span>
    </div>
  );
};

export const SkincareHighlights = () => {
  const P = useProduct();
  const { highlights } = P;
  if (!highlights) return null;

  const isV3 = !!P.v3;
  const isV31 = !!P.v31;
  const isV32 = !!P.v32;
  const items = highlights.items;
  // V3 mobile marquee: split into two rows (4 + rest)
  const row1 = items.slice(0, 4);
  const row2 = items.slice(4);

  return (
    <section className={`bg-white ${P.v34 ? "py-8 lg:py-10" : isV32 ? "pt-10 pb-24" : "py-24"}`} data-testid="skincare-highlights">
      <div className="max-w-[1600px] mx-auto px-8">
        <div className={`text-center ${P.v34 ? "" : "mb-14"}`}>
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-4">
            Why you can trust it
          </div>
          {!isV32 && (
            <h2 className="text-3xl lg:text-4xl tracking-tight font-semibold text-neutral-900">
              {highlights.heading}
            </h2>
          )}
        </div>

        {/* V3 mobile only — two opposing marquee rows */}
        {isV3 && (
          <div className={`lg:hidden -mx-8 space-y-4 ${isV31 ? "" : ""}`} data-testid="highlights-marquee">
            {/* Row 1: 4 items, right-to-left (default marquee) */}
            <div className="relative overflow-hidden">
              <div
                className="flex items-center animate-marquee-slow"
                style={{ width: "max-content" }}
                data-testid="highlights-marquee-row1"
              >
                {[...row1, ...row1, ...row1].map((item, i) => (
                  <IconChip key={`r1-${i}`} item={item} unframed={isV31} />
                ))}
              </div>
              {/* Edge fades to soften the loop seam */}
              <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
            </div>
            {/* Row 2: remaining items, left-to-right */}
            {row2.length > 0 && (
              <div className="relative overflow-hidden">
                <div
                  className="flex items-center animate-marquee-rev-slow"
                  style={{ width: "max-content" }}
                  data-testid="highlights-marquee-row2"
                >
                  {[...row2, ...row2, ...row2, ...row2].map((item, i) => (
                    <IconChip key={`r2-${i}`} item={item} unframed={isV31} />
                  ))}
                </div>
                <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
              </div>
            )}
          </div>
        )}

        {/* V3.1 DESKTOP — single-row editorial marquee, no frame around chips.
            Slow 70s cycle, hover-pauses, respects prefers-reduced-motion. */}
        {isV31 && (
          <div
            className="hidden lg:block relative overflow-hidden -mx-8 group"
            data-testid="highlights-marquee-desktop"
          >
            <div
              className="flex items-center animate-marquee-editorial [animation-play-state:running] group-hover:[animation-play-state:paused] motion-reduce:animate-none"
              style={{ width: "max-content" }}
            >
              {[...items, ...items, ...items].map((item, i) => (
                <IconChip key={`v31d-${i}`} item={item} unframed size="lg" />
              ))}
            </div>
            {/* Edge fades soften the loop seam. */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent" />
          </div>
        )}

        {/* Default grid — desktop (V1/V2/V3), and V1/V2 on mobile too.
            V3 hides on mobile (marquee above). V3.1 hides on desktop too
            (single-row marquee above). */}
        <div
          className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 border-l border-t border-neutral-200 ${
            isV31 ? "hidden" : isV3 ? "hidden lg:grid" : ""
          }`}
        >
          {items.map((item, i) => (
            <Reveal key={item.label} delay={(i % 7) * 0.06}>
              <div className="group h-full border-r border-b border-neutral-200 px-5 py-9 flex flex-col items-center text-center gap-4 hover:bg-riversol-paper transition-colors duration-300">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-riversol-blue/[0.06] group-hover:bg-riversol-blue/[0.1] transition-colors">
                  <img src={item.icon} alt="" aria-hidden="true" className="w-6 h-6" loading="lazy" />
                </span>
                <span className="text-[13px] font-medium leading-snug text-neutral-800">{item.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SkincareHighlights;
